/**
 * Adding a sign-in method to the signed-in account (docs/identity/ADR-001).
 *
 * Apple, Google and VK ID run their usual redirect flows; a flag in sessionStorage
 * tells the callback page to ADD the method instead of signing in. VK ID keeps
 * that intent in its own PKCE session instead (see vk-id-oauth.ts): its
 * round-trip can come back in a new tab, where sessionStorage is empty. Every add
 * answers with a fresh session: the survivor's when the method already
 * belonged to another account and the two were merged.
 */
import type { SignInMethodResult } from '../api/auth';
import { wipePersistedCachesIfIdentityChanged } from '../model/auth.store.internals';
import { useAuthStore } from '../model/auth.store';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { profileService } from '@/shared/api/services/profile.service';
import { AnalyticsEvent, trackEvent } from '@/shared/lib/analytics';
import { setSignedOut } from './device-identity';
import { ApiClientError } from '@/shared/lib/api';

const LINK_FLAG = 'oauth_link_method';
const MERGE_MESSAGE = 'sign_in_merge_message';
const LINK_ERROR = 'sign_in_link_error';
export const SIGN_IN_METHODS_PATH = '/account/sign-in-methods';

export type LinkableMethod = 'apple' | 'google' | 'vk' | 'email';

/** Mark the next OAuth callback as "add this method", not "sign in". */
export function markLinkFlow(method: 'apple' | 'google'): void {
  sessionStorage.setItem(LINK_FLAG, method);
}

/** Read and clear the link flag for the provider whose callback is running. */
export function consumeLinkFlow(method: 'apple' | 'google'): boolean {
  const flagged = sessionStorage.getItem(LINK_FLAG) === method;
  if (flagged) sessionStorage.removeItem(LINK_FLAG);
  return flagged;
}

/**
 * Run a sign-in method add (the request and storing its session) with 401s
 * from the old token ignored; see AuthTokenService.isSwitchingSession.
 */
export async function withSessionSwitch<T>(run: () => Promise<T>): Promise<T> {
  AuthTokenService.isSwitchingSession = true;
  try {
    return await run();
  } finally {
    AuthTokenService.isSwitchingSession = false;
  }
}

/** Store the new session, refresh the profile caches, log the funnel events. */
export async function applySignInMethodResult(
  result: SignInMethodResult,
  method: LinkableMethod
): Promise<void> {
  const { credentials, mergeMessage } = result;

  // Token first: the wipe clears the query cache, mounted queries refetch at
  // once, and after a merge the old token is already revoked, so a refetch on
  // it got 401 and signed the person out. The wipe compares stored users, not
  // tokens, so the order does not affect it.
  await AuthTokenService.setToken(credentials.token);
  await wipePersistedCachesIfIdentityChanged(credentials.user.id);
  await AuthTokenService.setUser(credentials.user);
  await profileService.applyProfileData(credentials.profileData, 'sign-in-method');
  useAuthStore.setState({ token: credentials.token, isAuthenticated: true });
  setSignedOut(false);

  trackEvent(AnalyticsEvent.SIGN_IN_METHOD_ADDED, { method });
  if (mergeMessage) {
    trackEvent(AnalyticsEvent.ACCOUNTS_MERGED, { method });
    sessionStorage.setItem(MERGE_MESSAGE, mergeMessage);
  }
}

/** One-shot message the sign-in methods screen shows after a merge. */
export function consumeMergeMessage(): string | null {
  const message = sessionStorage.getItem(MERGE_MESSAGE);
  if (message) sessionStorage.removeItem(MERGE_MESSAGE);
  return message;
}

/** The API's own refusal text, shown on the sign-in methods screen. */
export function rememberLinkError(error: unknown): void {
  const message = error instanceof ApiClientError ? error.getFirstError() : null;
  if (message) sessionStorage.setItem(LINK_ERROR, message);
}

export function consumeLinkError(): string | null {
  const message = sessionStorage.getItem(LINK_ERROR);
  if (message) sessionStorage.removeItem(LINK_ERROR);
  return message;
}
